import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import {
  getEnvironmentSettings,
  updateEnvironmentSettings,
  upsertEnvironmentSettings,
} from '@/services/environmentSettingsApi';
import type { EnvironmentSettingsPutPayload } from '@/services/environmentSettingsApi';
import { isLocalMode } from '@/services/localRuntimeClient';

/**
 * Fetches environment settings for an application.
 * Returns the settings array via React Query.
 */
export const useEnvironmentSettings = (projectName: string, appName: string) => {
  const { getOttoToken, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['environmentSettings', projectName, appName],
    queryFn: async () => {
      // In local mode the API omits the Authorization header, so skip the
      // async token fetch entirely. Matches the pattern in other hooks
      // (e.g., useAppsInProject).
      if (isLocalMode()) {
        return getEnvironmentSettings(projectName, appName, '');
      }
      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to acquire Otto token');
      }
      return getEnvironmentSettings(projectName, appName, token);
    },
    enabled: (isAuthenticated || isLocalMode()) && !!projectName && !!appName,
    staleTime: 30 * 1000, // 30 seconds
    // The env-vars editor holds unsaved local edits in component state; an
    // auto-refetch (focus change, reconnect, or stale interval) would replace
    // those edits with server data and silently discard the user's work.
    // Refetches still happen on explicit user action (Refresh). Saves update
    // the cache directly via setQueryData (no background refetch on save).
    refetchInterval: false,
  });
};

/**
 * Mutation hook to update environment settings.
 *
 * On success, updates the query cache directly with the PUT response via
 * `setQueryData` instead of invalidating + refetching. The ACA backend
 * re-deploys the container on every env-var write, so a GET fired right
 * after the PUT often returns stale (pre-deploy) data. Caching the PUT
 * response keeps the UI consistent without a second round-trip.
 */
export const useUpdateEnvironmentSettings = (projectName: string, appName: string) => {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: EnvironmentSettingsPutPayload) => {
      // Match the pattern used by other local-mode hooks: skip the token
      // fetch in local mode since the API ignores Authorization there.
      if (isLocalMode()) {
        return updateEnvironmentSettings(projectName, appName, '', payload);
      }
      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to acquire Otto token');
      }
      return updateEnvironmentSettings(projectName, appName, token, payload);
    },
    onSuccess: (data) => {
      // Update the query cache directly with the PUT response instead of
      // invalidating + refetching. The ACA backend re-deploys the container
      // on every env-var write; a GET fired immediately after the PUT often
      // returns stale data (before the deploy propagates), which overwrites
      // the user's freshly-saved state and makes new variables vanish.
      queryClient.setQueryData(
        ['environmentSettings', projectName, appName],
        data.properties.settings,
      );
    },
  });
};

/**
 * Mutation hook to upsert a small batch of environment settings without
 * disturbing the others. Used by flows (e.g. service-provider connection
 * creation) that need to add or update specific keys imperatively; the
 * `useUpdateEnvironmentSettings` hook above is a raw PUT with replace
 * semantics and is unsuitable for incremental writes.
 */
export const useUpsertEnvironmentSettings = (projectName: string, appName: string) => {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (additions: Record<string, string>) => {
      if (isLocalMode()) {
        return upsertEnvironmentSettings(projectName, appName, '', additions);
      }
      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to acquire Otto token');
      }
      return upsertEnvironmentSettings(projectName, appName, token, additions);
    },
    onSuccess: (settings) => {
      queryClient.setQueryData(
        ['environmentSettings', projectName, appName],
        settings,
      );
    },
  });
};
